import type { SmileSimulationLanguage } from "./types";

export const SMILE_SIMULATION_LANGUAGE_LABELS: Record<
  SmileSimulationLanguage,
  string
> = {
  ko: "한국어",
  en: "English",
  ja: "日本語",
};

export const isSmileSimulationLanguage = (
  value: unknown,
): value is SmileSimulationLanguage =>
  value === "ko" || value === "en" || value === "ja";

export const getSmileSimulationCopy = (language: SmileSimulationLanguage) =>
  language === "en" ? EN_COPY : language === "ja" ? JA_COPY : KO_COPY;

const KO_COPY = {
  messages: {
    simulationRequest: "스마일 디자인을 준비하고 있어요...",
    simulationSuccess: "이미지를 좌우로 드래그해서 비교해 보세요.",
    simulationFailure: "시뮬레이션 생성에 실패했어요. 다시 시도해 주세요.",
    clinicIdRequired:
      "클리닉 정보를 확인할 수 없어 시뮬레이션을 저장할 수 없어요.",
    simulationAuthRequired:
      "인증 정보를 확인할 수 없어 시뮬레이션을 저장할 수 없어요.",
    emailRequired: "이메일 또는 아이디를 입력해 주세요.",
    passwordRequired: "비밀번호를 입력해 주세요.",
    authFailed: "인증에 실패했습니다. 계정을 다시 확인해 주세요.",
    patientNameRequired: "환자명을 입력해 주세요.",
    chartNumberRequired: "차트번호를 입력해 주세요.",
    imageUploadFailure:
      "업로드한 이미지를 불러오지 못했어요. 다른 이미지를 시도해 주세요.",
    imageUploadType: "이미지 파일만 업로드할 수 있어요.",
    imageDownloadFailure: "이미지 다운로드에 실패했습니다. 다시 시도해 주세요.",
    clinicWatermarkUploadFailure:
      "클리닉 워터마크 업로드에 실패했어요. 다시 시도해 주세요.",
    clinicWatermarkUploadUnavailable:
      "클리닉 정보를 확인할 수 없어 워터마크를 업로드할 수 없어요.",
    watermarkPreviewFailure:
      "워터마크 미리보기를 불러오지 못했어요. 다시 시도해 주세요.",
  },
  progress: {
    faceMapping: "안면 해부학 구조 및 골격 맵핑 중...",
    ratioCalculation: "입술 라인과 치아의 황금 비율 산출 중...",
    smileDesign: "스마일 아치와 정중선 정밀 설계 중...",
    gumHarmony: "치은 라인 및 주변부 심미 조화 매칭 중...",
    textureRendering: "자연광 반사를 고려한 치아 질감 렌더링 중...",
    completed: "당신만을 위한 새로운 미소 디자인 완료.",
  },
  settings: {
    title: "시뮬레이션 설정",
    language: "언어 / Language",
    imageModel: "이미지 모델",
    prompt: "프롬프트",
    logout: "로그아웃",
  },
  home: {
    goHome: "홈으로 이동",
    title: "반갑습니다",
    accountLabel: "로그인한 클리닉 계정:",
    newSimulation: "새 스마일 시뮬레이션",
    history: "환자 기록 보기",
    todayCount: "오늘 생성한 기록",
    totalCount: "전체 환자 기록",
    recentPatients: "최근 작업 환자",
    viewAll: "전체 보기",
    recordUnit: "건",
    latestCreated: "최근 생성:",
    emptyTitle: "아직 생성된 기록이 없어요",
    emptyDescription:
      "새 스마일 시뮬레이션을 시작하면 환자 기록이 여기에 쌓입니다.",
    emptyAction: "첫 시뮬레이션 시작",
  },
  patientInfo: {
    title: "환자 정보를 입력해 주세요",
    accountLabel: "현재 클리닉 계정:",
    nameLabel: "환자명",
    namePlaceholder: "예: 홍길동",
    chartLabel: "차트번호",
    chartPlaceholder: "예: C-102938",
    next: "다음",
  },
  capture: {
    cameraLoading: "카메라를 불러오는 중이에요.",
    guide: "얼굴을 가이드라인에 맞춰주세요.",
    editPatient: "환자 정보 수정",
    currentPatient: "Current Patient",
    uploadBusy: "사진 업로드 중",
    upload: "사진 업로드",
    retake: "다시 찍기",
    retry: "다시 시도",
    startSimulation: "시뮬레이션 시작",
  },
  result: {
    completed: "시뮬레이션 생성이 완료되었습니다.",
    error: "시뮬레이션 생성에 실패했습니다.",
    idle: "찍은 사진을 확인해 주세요.",
    processing: "시뮬레이션을 만들고 있습니다.",
    downloading: "다운로드 중...",
    download: "다운로드",
    openPatientHistory: "환자 기록 보기",
  },
  history: {
    title: "환자 기록 보기",
    descriptionSuffix: "클리닉의 환자 기록을 검색하고 확인할 수 있어요.",
    searchPlaceholder: "환자명 또는 차트번호 검색",
    latestRecord: "최근 기록",
    noSearchTitle: "검색 결과가 없어요",
    noSearchDescription: "다른 환자명 또는 차트번호로 검색해 보세요.",
    emptyTitle: "아직 환자 기록이 없어요",
    emptyDescription:
      "새 스마일 시뮬레이션을 완료하면 환자 기록이 여기에 표시됩니다.",
    emptyAction: "새 시뮬레이션 시작",
  },
  patientDetail: {
    list: "목록",
    newSimulation: "새 시뮬레이션",
    title: "환자 기록 상세",
    caption: "Selected Patient",
    totalRecords: "총 기록 수",
    status: "상태:",
    completed: "완료",
    emptyTitle: "아직 기록이 없어요",
    emptyDescription: "이 환자에 대한 첫 스마일 시뮬레이션을 시작해 보세요.",
  },
  recordDetail: {
    back: "환자 기록",
    createdAt: "생성 시각:",
    imageModel: "이미지 모델",
    prompt: "프롬프트",
    unknown: "알 수 없음",
    downloading: "다운로드 중...",
    download: "다운로드",
  },
  download: {
    title: "다운로드 미리보기",
    serviceWatermark: "Just Scan 워터마크",
    clinicWatermark: "클리닉 워터마크",
    preparing: "워터마크 미리보기를 준비하고 있어요.",
    uploading: "업로드 중...",
    uploadWatermark: "워터마크 업로드",
    cancel: "취소",
    downloading: "다운로드 중...",
    download: "다운로드",
  },
  summary: {
    patient: "Patient",
    chartNo: "Chart No.",
  },
};

const EN_COPY: typeof KO_COPY = {
  messages: {
    simulationRequest: "Preparing your smile design...",
    simulationSuccess: "Drag left and right to compare the images.",
    simulationFailure: "Failed to create the simulation. Please try again.",
    clinicIdRequired:
      "Clinic information could not be verified, so the simulation cannot be saved.",
    simulationAuthRequired:
      "Authentication could not be verified, so the simulation cannot be saved.",
    emailRequired: "Please enter your email or username.",
    passwordRequired: "Please enter your password.",
    authFailed: "Authentication failed. Please check your account.",
    patientNameRequired: "Please enter the patient name.",
    chartNumberRequired: "Please enter the chart number.",
    imageUploadFailure:
      "Could not load the uploaded image. Please try another image.",
    imageUploadType: "Only image files can be uploaded.",
    imageDownloadFailure: "Failed to download the image. Please try again.",
    clinicWatermarkUploadFailure:
      "Failed to upload the clinic watermark. Please try again.",
    clinicWatermarkUploadUnavailable:
      "Clinic information could not be verified, so the watermark cannot be uploaded.",
    watermarkPreviewFailure:
      "Failed to load the watermark preview. Please try again.",
  },
  progress: {
    faceMapping: "Mapping facial anatomy and skeletal structure...",
    ratioCalculation:
      "Calculating the golden ratio between the lip line and teeth...",
    smileDesign: "Precisely designing the smile arch and midline...",
    gumHarmony:
      "Matching the gingival line with the surrounding esthetic balance...",
    textureRendering:
      "Rendering tooth texture with natural light reflection in mind...",
    completed: "Your personalized new smile design is complete.",
  },
  settings: {
    title: "Simulation Settings",
    language: "언어 / Language",
    imageModel: "Image Model",
    prompt: "Prompt",
    logout: "Log out",
  },
  home: {
    goHome: "Go home",
    title: "Welcome",
    accountLabel: "Signed-in clinic account:",
    newSimulation: "New Smile Simulation",
    history: "Patient History",
    todayCount: "Created Today",
    totalCount: "All Patient Records",
    recentPatients: "Recent Patients",
    viewAll: "View All",
    recordUnit: "records",
    latestCreated: "Latest:",
    emptyTitle: "No records yet",
    emptyDescription:
      "Patient records will appear here after you start a new Smile Simulation.",
    emptyAction: "Start First Simulation",
  },
  patientInfo: {
    title: "Enter patient information",
    accountLabel: "Current clinic account:",
    nameLabel: "Patient Name",
    namePlaceholder: "e.g. John Doe",
    chartLabel: "Chart Number",
    chartPlaceholder: "e.g. C-102938",
    next: "Next",
  },
  capture: {
    cameraLoading: "Loading the camera.",
    guide: "Align your face with the guide before taking a photo.",
    editPatient: "Edit Patient Info",
    currentPatient: "Current Patient",
    uploadBusy: "Uploading photo",
    upload: "Upload photo",
    retake: "Retake",
    retry: "Try Again",
    startSimulation: "Start Simulation",
  },
  result: {
    completed: "Simulation is complete.",
    error: "Failed to create the simulation.",
    idle: "Please review the captured photo.",
    processing: "Creating the simulation.",
    downloading: "Downloading...",
    download: "Download",
    openPatientHistory: "View Patient History",
  },
  history: {
    title: "Patient History",
    descriptionSuffix: "clinic patient records can be searched and reviewed.",
    searchPlaceholder: "Search by patient name or chart number",
    latestRecord: "Latest record",
    noSearchTitle: "No search results",
    noSearchDescription:
      "Try searching with another patient name or chart number.",
    emptyTitle: "No patient records yet",
    emptyDescription:
      "Patient records will appear here after you complete a new Smile Simulation.",
    emptyAction: "Start New Simulation",
  },
  patientDetail: {
    list: "List",
    newSimulation: "New Simulation",
    title: "Patient Record Detail",
    caption: "Selected Patient",
    totalRecords: "Total Records",
    status: "Status:",
    completed: "Completed",
    emptyTitle: "No records yet",
    emptyDescription: "Start the first Smile Simulation for this patient.",
  },
  recordDetail: {
    back: "Patient Records",
    createdAt: "Created at:",
    imageModel: "Image Model",
    prompt: "Prompt",
    unknown: "Unknown",
    downloading: "Downloading...",
    download: "Download",
  },
  download: {
    title: "Download Preview",
    serviceWatermark: "Just Scan Watermark",
    clinicWatermark: "Clinic Watermark",
    preparing: "Preparing the watermark preview.",
    uploading: "Uploading...",
    uploadWatermark: "Upload Watermark",
    cancel: "Cancel",
    downloading: "Downloading...",
    download: "Download",
  },
  summary: {
    patient: "Patient",
    chartNo: "Chart No.",
  },
};

const JA_COPY: typeof KO_COPY = {
  messages: {
    simulationRequest: "スマイルデザインを準備しています...",
    simulationSuccess: "画像を左右にドラッグして比較してください。",
    simulationFailure:
      "シミュレーションの作成に失敗しました。もう一度お試しください。",
    clinicIdRequired:
      "クリニック情報を確認できないため、シミュレーションを保存できません。",
    simulationAuthRequired:
      "認証情報を確認できないため、シミュレーションを保存できません。",
    emailRequired: "メールアドレスまたはIDを入力してください。",
    passwordRequired: "パスワードを入力してください。",
    authFailed: "認証に失敗しました。アカウントを確認してください。",
    patientNameRequired: "患者名を入力してください。",
    chartNumberRequired: "カルテ番号を入力してください。",
    imageUploadFailure:
      "アップロードした画像を読み込めませんでした。別の画像をお試しください。",
    imageUploadType: "画像ファイルのみアップロードできます。",
    imageDownloadFailure:
      "画像のダウンロードに失敗しました。もう一度お試しください。",
    clinicWatermarkUploadFailure:
      "クリニックのウォーターマークのアップロードに失敗しました。もう一度お試しください。",
    clinicWatermarkUploadUnavailable:
      "クリニック情報を確認できないため、ウォーターマークをアップロードできません。",
    watermarkPreviewFailure:
      "ウォーターマークのプレビューを読み込めませんでした。もう一度お試しください。",
  },
  progress: {
    faceMapping: "顔の解剖学的構造と骨格をマッピングしています...",
    ratioCalculation: "唇のラインと歯の黄金比を算出しています...",
    smileDesign: "スマイルアーチと正中線を精密に設計しています...",
    gumHarmony: "歯肉ラインと周辺部の審美的な調和を合わせています...",
    textureRendering:
      "自然光の反射を考慮しながら歯の質感をレンダリングしています...",
    completed: "あなただけの新しいスマイルデザインが完成しました。",
  },
  settings: {
    title: "シミュレーション設定",
    language: "言語 / Language",
    imageModel: "画像モデル",
    prompt: "プロンプト",
    logout: "ログアウト",
  },
  home: {
    goHome: "ホームへ移動",
    title: "ようこそ",
    accountLabel: "ログイン中のクリニックアカウント:",
    newSimulation: "新しいスマイルシミュレーション",
    history: "患者履歴を見る",
    todayCount: "本日作成した記録",
    totalCount: "全患者記録",
    recentPatients: "最近の患者",
    viewAll: "すべて見る",
    recordUnit: "件",
    latestCreated: "最新作成:",
    emptyTitle: "まだ記録がありません",
    emptyDescription:
      "新しいスマイルシミュレーションを開始すると、患者記録がここに表示されます。",
    emptyAction: "最初のシミュレーションを開始",
  },
  patientInfo: {
    title: "患者情報を入力してください",
    accountLabel: "現在のクリニックアカウント:",
    nameLabel: "患者名",
    namePlaceholder: "例: 山田太郎",
    chartLabel: "カルテ番号",
    chartPlaceholder: "例: C-102938",
    next: "次へ",
  },
  capture: {
    cameraLoading: "カメラを読み込んでいます。",
    guide: "顔をガイドラインに合わせてください。",
    editPatient: "患者情報を編集",
    currentPatient: "現在の患者",
    uploadBusy: "写真をアップロード中",
    upload: "写真をアップロード",
    retake: "撮り直す",
    retry: "もう一度試す",
    startSimulation: "シミュレーションを開始",
  },
  result: {
    completed: "シミュレーションの作成が完了しました。",
    error: "シミュレーションの作成に失敗しました。",
    idle: "撮影した写真を確認してください。",
    processing: "シミュレーションを作成しています。",
    downloading: "ダウンロード中...",
    download: "ダウンロード",
    openPatientHistory: "患者履歴を見る",
  },
  history: {
    title: "患者履歴を見る",
    descriptionSuffix: "クリニックの患者記録を検索して確認できます。",
    searchPlaceholder: "患者名またはカルテ番号で検索",
    latestRecord: "最新記録",
    noSearchTitle: "検索結果がありません",
    noSearchDescription: "別の患者名またはカルテ番号で検索してください。",
    emptyTitle: "まだ患者記録がありません",
    emptyDescription:
      "新しいスマイルシミュレーションを完了すると、患者記録がここに表示されます。",
    emptyAction: "新しいシミュレーションを開始",
  },
  patientDetail: {
    list: "一覧",
    newSimulation: "新しいシミュレーション",
    title: "患者記録詳細",
    caption: "選択中の患者",
    totalRecords: "総記録数",
    status: "ステータス:",
    completed: "完了",
    emptyTitle: "まだ記録がありません",
    emptyDescription:
      "この患者の最初のスマイルシミュレーションを開始してください。",
  },
  recordDetail: {
    back: "患者記録",
    createdAt: "作成日時:",
    imageModel: "画像モデル",
    prompt: "プロンプト",
    unknown: "不明",
    downloading: "ダウンロード中...",
    download: "ダウンロード",
  },
  download: {
    title: "ダウンロードプレビュー",
    serviceWatermark: "Just Scan ウォーターマーク",
    clinicWatermark: "クリニックウォーターマーク",
    preparing: "ウォーターマークのプレビューを準備しています。",
    uploading: "アップロード中...",
    uploadWatermark: "ウォーターマークをアップロード",
    cancel: "キャンセル",
    downloading: "ダウンロード中...",
    download: "ダウンロード",
  },
  summary: {
    patient: "患者",
    chartNo: "カルテ番号",
  },
};
